import React, { useEffect, useState } from "react";
import { Storage } from "aws-amplify";
import { localStorageGet } from "../../util/localStorage";

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const storageGet = (key: string, opts: any): Promise<any> =>
  isLocalAuth ? localStorageGet(key, opts) : Storage.get(key, opts);

const esc = (v: any): string =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const getTemplatePath = (name: string) =>
  `/templates/${String(name || "").trim().toLowerCase()}.html`;

const getVideoPreviewRow = (video: any) => {
  if (!video?.thumbnailUrl) return "";
  const title = esc(video?.title || "Video preview");
  const thumbnailUrl = esc(video.thumbnailUrl);

  return `<tr>
            <td align="center" class="video-cell" bgcolor="#000000" style="background-color:#000000; padding:0;">
              <a href="https://example.com/video-landing-page" target="_blank" style="display:block; position:relative; text-decoration:none; line-height:0;">
                <img src="${thumbnailUrl}" width="600" alt="${title}" style="display:block; width:100%; max-width:600px; height:auto; border:0; line-height:100%; outline:none; text-decoration:none;">
                <span style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); width:80px; height:80px; border-radius:40px; background-color:#ffffff; color:#fdb516; font-family:Arial, Helvetica, sans-serif; font-size:36px; line-height:80px; font-weight:700; text-align:center;">&#9654;</span>
              </a>
            </td>
          </tr>`;
};

const replaceVideoPlaceholder = (html: string, video: any) => {
  const videoRow = getVideoPreviewRow(video);
  if (!videoRow) return html;

  return html.replace(
    /<tr>\s*<td[^>]*class="video-cell"[\s\S]*?<\/tr>\s*(?=<tr>\s*<td[^>]*class="content-cell")/,
    videoRow
  );
};

const replaceDescription = (html: string, video: any) => {
  const description = String(video?.description || "").trim();
  const descriptionRow =
    /<tr>\s*<td[^>]*class="body-copy"[\s\S]*?<\/td>\s*<\/tr>/;

  if (!description) {
    return html.replace(descriptionRow, "");
  }

  return html.replace(
    /Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua\. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat\. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur\./g,
    esc(description)
  );
};

const getSocialIconStyle = (html: string) => {
  const match = html.match(/class="social-icon" style="([^"]+)"/);
  return match?.[1] || "width:36px; height:36px; border-radius:18px; background:#3796ce; background-color:#3796ce; font-family:Arial, Helvetica, sans-serif; font-size:18px; line-height:18px; font-weight:700; color:#ffffff;";
};

const getFooterTitleStyle = (html: string) => {
  const match = html.match(/class="footer-title" style="([^"]+)"/);
  return match?.[1] || "font-family:Arial, Helvetica, sans-serif; font-size:20px; line-height:22px; font-weight:700; color:#4c6f86; padding:0 0 12px 0;";
};

const socialCell = (url: string, label: string, glyph: string, style: string) => {
  if (!url) return "";
  return `<td class="social-gap" style="padding:0 5px;">
            <a href="${esc(url)}" target="_blank" aria-label="${label}" style="text-decoration:none;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
                <tr>
                  <td width="36" height="36" align="center" valign="middle" class="social-icon" style="${style}">${glyph}</td>
                </tr>
              </table>
            </a>
          </td>`;
};

const replaceSocialFooter = (html: string, video: any) => {
  const user = video?.user || {};
  const iconStyle = getSocialIconStyle(html);
  const titleStyle = getFooterTitleStyle(html);
  const socialRows = [
    socialCell(user.facebookAddress, "Facebook", "f", iconStyle.replace(/font-size:\s*\d+px/, "font-size:23px")),
    socialCell(user.twitterAddress, "X", "X", iconStyle.replace(/font-size:\s*\d+px/, "font-size:18px")),
    socialCell(user.youtubeAddress, "YouTube", "&#9654;", iconStyle.replace(/font-size:\s*\d+px/, "font-size:18px")),
    socialCell(user.linkedinAddress, "LinkedIn", "in", iconStyle.replace(/font-size:\s*\d+px/, "font-size:18px")),
  ].filter(Boolean).join("");

  const titleAndSocialRows =
    /<tr>\s*<td[^>]*class="footer-title"[\s\S]*?<\/tr>\s*<tr>\s*<td align="center" style="padding-bottom:18px;">[\s\S]*?<\/tr>\s*(?=<tr>\s*<td align="center" style="padding-bottom:10px;">)/;

  if (!socialRows) {
    return html.replace(titleAndSocialRows, "");
  }

  return html.replace(
    titleAndSocialRows,
    `<tr>
       <td align="center" class="footer-title" style="${titleStyle}">
         Connect with me on
       </td>
     </tr>
     <tr>
       <td align="center" style="padding-bottom:18px;">
         <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
           <tr>${socialRows}</tr>
         </table>
       </td>
     </tr>`
  );
};

const prepareTemplateHtml = (html: string, video: any) => {
  let prepared = html
    .replace(/src="assets\//g, 'src="/templates/assets/')
    .replace(/background="assets\//g, 'background="/templates/assets/')
    .replace(/url\('assets\//g, "url('/templates/assets/")
    .replace(/Your Video Title \/ Headline Here/g, esc(video?.title || "Your Video Title / Headline Here"));
  prepared = replaceDescription(prepared, video);
  prepared = replaceVideoPlaceholder(prepared, video);
  return replaceSocialFooter(prepared, video);
};

const TemplateIs = ({ tempName, video, profile }: any) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const [profileImageUrl, setProfileImageUrl] = useState<string>("");
  const [templateHtml, setTemplateHtml] = useState<string>("");

  useEffect(() => {
    if (video?.thumbnail) {
      storageGet(video.thumbnail, { level: "protected" }).then(
        (url: string) => setThumbnailUrl(url)
      );
    }
  }, [video?.thumbnail]);

  useEffect(() => {
    const key = profile?.user?.url;
    if (key) {
      storageGet(key, { level: "private" }).then(
        (url: string) => setProfileImageUrl(url)
      );
    }
  }, [profile?.user?.url]);

  useEffect(() => {
    if (!tempName) {
      setTemplateHtml("");
      return;
    }
    fetch(getTemplatePath(tempName))
      .then(response => (response.ok ? response.text() : ""))
      .then(setTemplateHtml)
      .catch(() => setTemplateHtml(""));
  }, [tempName]);

  const user = profile?.user;
  const videoWithUrl = {
    ...video,
    thumbnailUrl: thumbnailUrl || "",
    profileImageUrl: profileImageUrl || "",
    profileName: user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "",
    jobTitle: user?.jobTitle || "",
    user,
  };
  const html = templateHtml ? prepareTemplateHtml(templateHtml, videoWithUrl) : "";

  return (
    <div className="showtemplate">
      {html ? (
        <iframe
          title={`${tempName} template preview`}
          srcDoc={html}
          style={{
            width: "100%",
            height: "820px",
            border: 0,
            display: "block",
            background: "transparent",
          }}
        />
      ) : (
        <p style={{ textAlign: "center", color: "#7a8a96", padding: "2em" }}>
          Template not found.
        </p>
      )}
    </div>
  );
};

export default TemplateIs;
